import { Link, createFileRoute } from '@tanstack/react-router';
import {
  ArrowRight,
  Bot,
  FileText,
  LayoutDashboard,
  LogIn,
  ServerCog,
  SwatchBook,
  Tag,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { pagesQuery } from '@/lib/customisation/queries';
import { useSiteDraft } from '@/lib/customisation/site-draft';
import { timeAgo } from '@/lib/format';
import { Route as StudioRoute, useStudioTenant } from '../customisation';

export const Route = createFileRoute('/_app/admin/customisation/')({ component: StudioOverview });

/** Where to start: each area with a one-line status and a way in. */
function StudioOverview() {
  const draft = useSiteDraft();
  const tenant = useStudioTenant();
  const search = StudioRoute.useSearch();
  const pages = useQuery(pagesQuery(tenant));
  const { branding, theme, login, dashboard } = draft.site;
  const colours = [...Object.values(theme.light), ...Object.values(theme.dark)].filter(
    Boolean,
  ).length;
  const logos = Object.values(branding.logos).filter(Boolean).length;
  const published = pages.data?.pages.filter((p) => p.publishedAt).length ?? 0;
  const areas: {
    to:
      | '/admin/customisation/branding'
      | '/admin/customisation/theme'
      | '/admin/customisation/login'
      | '/admin/customisation/dashboard'
      | '/admin/customisation/server'
      | '/admin/customisation/pages'
      | '/admin/customisation/ai';
    icon: typeof Tag;
    title: string;
    status: string;
    body: string;
  }[] = [
    {
      to: '/admin/customisation/branding',
      icon: Tag,
      title: 'Branding',
      status: branding.displayName
        ? `${branding.displayName} · ${logos} logo${logos === 1 ? '' : 's'}`
        : 'Hearth defaults',
      body: 'Logos, favicon, names, links and footer text.',
    },
    {
      to: '/admin/customisation/theme',
      icon: SwatchBook,
      title: 'Theme',
      status: colours
        ? `${colours} colour${colours === 1 ? '' : 's'} set · ${theme.typography.body}`
        : 'Hearth palette',
      body: 'Colours for dark and light mode, fonts, shape and density.',
    },
    {
      to: '/admin/customisation/login',
      icon: LogIn,
      title: 'Login page',
      status: login.heading ? login.heading : 'Default sign-in',
      body: 'Heading, background, promotional panel and links.',
    },
    {
      to: '/admin/customisation/dashboard',
      icon: LayoutDashboard,
      title: 'Dashboard',
      status: `${dashboard.nodes.length} top-level block${dashboard.nodes.length === 1 ? '' : 's'}`,
      body: 'Drag and drop what customers see on their overview.',
    },
    {
      to: '/admin/customisation/server',
      icon: ServerCog,
      title: 'Server pages',
      status: `Overview ${draft.site.serverOverview.nodes.length} · console ${draft.site.serverConsole.nodes.length} blocks`,
      body: 'Arrange every server’s overview and console tabs; the console can’t be removed.',
    },
    {
      to: '/admin/customisation/pages',
      icon: FileText,
      title: 'Pages',
      status: pages.data
        ? `${pages.data.pages.length} page${pages.data.pages.length === 1 ? '' : 's'} · ${published} published`
        : '…',
      body: 'Guides, rules, support and announcements as their own pages.',
    },
    {
      to: '/admin/customisation/ai',
      icon: Bot,
      title: 'AI builder',
      status: 'Describe it, review it, publish it',
      body: 'Let Hearth AI draft layouts, pages and branding changes for you to approve.',
    },
  ];
  return (
    <div className="mx-auto max-w-[1100px] px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Make the panel yours</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Everything here is a draft until you publish.{' '}
            {draft.state.publishedAt
              ? `Last published ${timeAgo(draft.state.publishedAt)}.`
              : 'Customers see Hearth’s defaults until the first publish.'}
          </p>
        </div>
        <Badge variant={draft.state.hasUnpublishedChanges ? 'warning' : 'success'}>
          {draft.state.hasUnpublishedChanges ? 'Unpublished changes' : 'Up to date'}
        </Badge>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {areas.map((a) => (
          <Link
            key={a.to}
            to={a.to}
            search={search}
            className="group focus-visible:ring-ring/50 rounded-xl outline-none focus-visible:ring-[3px]"
          >
            <Card className="group-hover:border-primary/40 h-full gap-3 px-5 py-5 transition-colors">
              <div className="flex items-center gap-3">
                <span className="bg-brand/12 text-brand grid size-9 place-items-center rounded-lg">
                  <a.icon className="size-4" aria-hidden />
                </span>
                <div className="min-w-0">
                  <p className="font-semibold">{a.title}</p>
                  <p className="text-muted-foreground truncate text-xs">{a.status}</p>
                </div>
                <ArrowRight
                  className="text-subtle group-hover:text-foreground ml-auto size-4 transition-colors"
                  aria-hidden
                />
              </div>
              <p className="text-muted-foreground text-sm">{a.body}</p>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
